import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import React, { createContext, PropsWithChildren } from 'react';

import { GameContext } from './game';
import { PlayerContext } from './player';
import { PostAsync } from '../services/api';
import { HubResponse } from '../types/responses/hub-response';
import { TradeRequest } from '../types/responses/trade-response';
import { AcceptTradeItems } from '../utils/game-engine/inventory';
import { UpdatePlayerStateAsync } from '../utils/game-engine/base';
import { NotificationContent, NotificationContext } from './notification';
import { deleteFromStore, getFromStore, putToStore } from '../utils/indexedDB';

export type TradeItem = {
  itemId: string;
  quantity: number;
};

export interface TradeState extends TradeRequest {
  showNotification: boolean;
  offeredItems: TradeItem[];
  receivedItems?: TradeItem[];
}

interface TradeUpdated extends TradeRequest {
  tradeOfferItems?: {
    itemId: string;
    quantity: number;
    isOffered: boolean;
  }[];
}

export const initialTradeStateContext: TradeState = {
  id: 0,
  gameCode: '',
  offerPlayerCode: '',
  offerPlayerStatus: '',
  requestPlayerCode: '',
  requestPlayerStatus: '',
  showNotification: false,
  offeredItems: []
};

const TradeStateContext = createContext<
  [
    TradeState,
    React.Dispatch<React.SetStateAction<TradeState>>,
    HubConnection | null
  ]
>([initialTradeStateContext, () => {}, null]);

interface TradeStateProviderProps extends PropsWithChildren {
  gameCode: string;
}

const TradeStateProvider = ({
  gameCode,
  children
}: TradeStateProviderProps) => {
  const [game] = React.useContext(GameContext);
  const [notification, setNotification] = React.useContext(NotificationContext);
  const [signalRHub, setSignalRHub] = React.useState<HubConnection | null>(
    null
  );
  const [tradeState, setTradeState] = React.useState<TradeState>({
    id: 0,
    gameCode,
    offerPlayerCode: '',
    offerPlayerStatus: '',
    requestPlayerCode: '',
    requestPlayerStatus: '',
    showNotification: false,
    offeredItems: []
  });

  const playerList = game.gameState?.players ?? [];
  const [player, setPlayer] = React.useContext(PlayerContext);
  const playerCode = player.playerState?.code;

  const tradeToast = (
    type: 'rejected' | 'cancelled' | 'accepted',
    code?: string
  ) => {
    if (code !== undefined && playerCode === code) return;

    let message = '';
    const playerName = playerList.find((p) => p.code === code)?.name;
    switch (type) {
      case 'rejected':
        message = `${playerName} declined your trade request.`;
        break;
      case 'cancelled':
        message = `The trade has been cancelled.`;
        break;
      case 'accepted':
        message = `The trade has been finished.`;
        break;
    }

    const content: NotificationContent[] = notification.content;
    content.push({
      icon: 'notifications',
      isHide: false,
      message: `${message}`
    });
    setNotification({ ...notification, content });
  };

  React.useEffect(() => {
    // handle accepted trade when both player accepted the trade
    if (
      [tradeState.offerPlayerStatus, tradeState.requestPlayerStatus].every(
        (s) => s === 'Accepted'
      )
    ) {
      const givenItems = tradeState.offeredItems;
      const receivedItems = tradeState.receivedItems ?? [];
      setPlayer((prev) => {
        if (!prev.playerState) return prev;
        const result = AcceptTradeItems(
          prev.playerState!,
          givenItems,
          receivedItems
        );

        return UpdatePlayerStateAsync(prev, result);
      });

      setTradeState(initialTradeStateContext);
      deleteFromStore('trade', gameCode);
    }
  }, [tradeState.offerPlayerStatus, tradeState.requestPlayerStatus, gameCode]);

  React.useEffect(() => {
    if (!gameCode && !playerCode) return;
    let isCanceled = false;

    getFromStore<TradeState>('trade', gameCode).then((trade) => {
      if (trade) setTradeState(trade);

      PostAsync(`/hubs/trade/${gameCode}/users/${playerCode}`, {}).then(
        (resp) => {
          const { accessToken, endpointUrl } = resp as HubResponse;

          if (accessToken && endpointUrl) {
            const hubConnectionSetup = new HubConnectionBuilder();
            hubConnectionSetup.withUrl(endpointUrl, {
              accessTokenFactory: () => accessToken
            });
            hubConnectionSetup.withAutomaticReconnect();

            const hubConnection = hubConnectionSetup.build();
            hubConnection
              .start()
              .then(() => {
                if (isCanceled) return hubConnection.stop();

                hubConnection.on('TradeRequested', (trade: TradeRequest) => {
                  const content = {
                    ...trade,
                    showNotification: true,
                    offeredItems: []
                  };
                  setTradeState(content);
                  putToStore('trade', content);
                });

                hubConnection.on('TradeRejected', (trade: TradeRequest) => {
                  if (trade.gameCode !== gameCode) return;
                  tradeToast('rejected', trade.requestPlayerCode);
                });

                hubConnection.on('TradeStarted', (trade: TradeRequest) => {
                  if (trade.gameCode !== gameCode) return;
                  const content = {
                    ...trade,
                    showNotification: false,
                    offeredItems: []
                  };
                  setTradeState(content);
                  putToStore('trade', content);
                });

                hubConnection.on('TradeCanceled', (trade: TradeRequest) => {
                  if (trade.gameCode !== gameCode) return;
                  tradeToast('cancelled');
                  setTradeState(initialTradeStateContext);
                  deleteFromStore('trade', gameCode);
                });

                hubConnection.on('TradeAccepted', (trade: TradeRequest) => {
                  // enter this event only when both player accepted the trade
                  if (trade.gameCode !== gameCode) return;
                  tradeToast('accepted');
                  setTradeState((prev) => {
                    const content = {
                      ...prev,
                      ...trade
                    };
                    putToStore('trade', content);
                    return content;
                  });
                });

                hubConnection.on(
                  'TradeOfferItemsUpdated',
                  (trade: TradeUpdated) => {
                    if (trade.gameCode !== gameCode) return;
                    const receivedItems = (trade.tradeOfferItems ?? [])
                      .filter((i) => i.isOffered)
                      .map((i) => ({
                        itemId: i.itemId,
                        quantity: i.quantity
                      }));
                    delete trade.tradeOfferItems;
                    setTradeState((prev) => {
                      const content = {
                        ...prev,
                        ...trade,
                        ...(trade.requestPlayerCode === playerCode && {
                          receivedItems
                        })
                      };
                      putToStore('trade', content);
                      return content;
                    });
                  }
                );

                hubConnection.on(
                  'TradeRespondItemsUpdated',
                  (trade: TradeUpdated) => {
                    if (trade.gameCode !== gameCode) return;
                    const receivedItems = (trade.tradeOfferItems ?? [])
                      .filter((i) => !i.isOffered)
                      .map((i) => ({
                        itemId: i.itemId,
                        quantity: i.quantity
                      }));
                    delete trade.tradeOfferItems;
                    setTradeState((prev) => {
                      const content = {
                        ...prev,
                        ...trade,
                        ...(trade.offerPlayerCode === playerCode && {
                          receivedItems
                        })
                      };
                      putToStore('trade', content);
                      return content;
                    });
                  }
                );

                hubConnection.on(
                  'TradeStatusUpdated',
                  (trade: TradeRequest) => {
                    if (trade.gameCode !== gameCode) return;
                    setTradeState((prev) => {
                      const content = {
                        ...prev,
                        ...trade
                      };
                      putToStore('trade', content);
                      return content;
                    });
                  }
                );

                setSignalRHub(hubConnection);
              })
              .catch((error) => {
                if (isCanceled) return;
              });

            return () => {
              isCanceled = true;

              if (hubConnection.state === HubConnectionState.Connected)
                hubConnection.stop();

              setSignalRHub(null);
            };
          }
        }
      );
    });
  }, [gameCode, playerCode]);

  return (
    <TradeStateContext.Provider value={[tradeState, setTradeState, signalRHub]}>
      {children}
    </TradeStateContext.Provider>
  );
};

export { TradeStateProvider, TradeStateContext };
